import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogImage?: string;
}

export default function SEOHead({
  title,
  description,
  canonical,
  keywords = 'Redcubical Systems, HealHub, Suraksha, Vedant, software company India, clinic management software, anti fraud app India, AI health assistant',
  ogImage = 'https://redcubicalsystems.com/logo.png',
}: SEOHeadProps) {
  const fullTitle = `${title} | Redcubical Systems`;
  const url = canonical
    ? `https://redcubicalsystems.com${canonical}`
    : 'https://redcubicalsystems.com';

  return (
    <Helmet>
      {/* ── Primary SEO ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Redcubical Systems" />
      <link rel="canonical" href={url} />

      {/* ── Open Graph (Facebook / LinkedIn) ── */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Redcubical Systems" />
      <meta property="og:locale" content="en_IN" />

      {/* ── Twitter / X ── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* ── Schema.org JSON-LD ── */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Redcubical Systems',
          url: 'https://redcubicalsystems.com',
          logo: 'https://redcubicalsystems.com/logo.png',
          description:
            'Redcubical Systems builds innovative software products for healthcare and cybersecurity in India.',
          sameAs: ['https://healhub.in'],
          areaServed: {
            '@type': 'Country',
            name: 'India',
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Software Products',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'SoftwareApplication',
                  name: 'HealHub',
                  description:
                    'Complete clinic management software for India',
                  applicationCategory: 'HealthApplication',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'SoftwareApplication',
                  name: 'Suraksha',
                  description:
                    'AI-powered anti-fraud protection app for India',
                  applicationCategory: 'SecurityApplication',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'SoftwareApplication',
                  name: 'Vedant',
                  description:
                    'AI health assistant for Indian patients',
                  applicationCategory: 'HealthApplication',
                },
              },
            ],
          },
        })}
      </script>
    </Helmet>
  );
}